import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { Alert } from '@/components/ui';
import { LocalePicker } from '@/components/locale-picker';
import { LogoMark } from '@/components/logo-mark';

type Status = 'working' | 'done' | 'error';

/**
 * Where a broadcast email's unsubscribe link lands.
 *
 * Unlike `/verify-email`, the person here usually has no TABITA account at
 * all — they are a contact in a church's directory, not a signed-in user —
 * so this never assumes a session or offers to "continue to the app".
 */
export function UnsubscribePage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [status, setStatus] = useState<Status>(token ? 'working' : 'error');
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    void unsubscribe();

    async function unsubscribe(): Promise<void> {
      try {
        await peopleApi.unsubscribe(token);
        setStatus('done');
      } catch {
        // Deliberately generic: the server never distinguishes a bad token
        // from any other failure here, and neither should this page.
        setStatus('error');
      }
    }
  }, [token]);

  return (
    <main className="auth-page">
      <div className="auth-card">
        <header className="auth-header">
          <LogoMark size={40} />
          <h1 className="auth-title">TABITA</h1>
        </header>

        {status === 'working' && <p>{t('communications.unsubscribe.working')}</p>}

        {status === 'done' && <Alert tone="success">{t('communications.unsubscribe.done')}</Alert>}

        {status === 'error' && <Alert tone="danger">{t('communications.unsubscribe.error')}</Alert>}

        <footer className="auth-footer">
          <Link to="/login" className="auth-secondary-link">
            {t('auth.signIn')}
          </Link>
          <LocalePicker />
        </footer>
      </div>
    </main>
  );
}
